import { skipToken } from '@reduxjs/toolkit/query/react';
import { buildAccounts } from 'app/shared/util/accounts-utils';
import { UserRead } from 'app/store/reducers/api/config-manager-cloud-services/store/types';
import {
  useGetCurrentUserAccountsQuery,
  userProfilesApi,
} from 'app/store/reducers/api/config-manager-cloud-services/store/user-profiles';
import { useMemo } from 'react';

function sortAlphabetically(a, b) {
  const x = a.label.toLowerCase();
  const y = b.label.toLowerCase();
  if (x < y) {
    return -1;
  }
  if (x > y) {
    return 1;
  }
  return 0;
}

// Order accounts so each sub-account appears directly under its parent (parents A→Z, their
// children A→Z beneath them); children whose parent isn't in the list fall to the end.
function groupByParent(options) {
  const byParent = new Map<string, any[]>();
  const tops: any[] = [];
  options.forEach(o => {
    if (o.parentAccountId) {
      if (!byParent.has(o.parentAccountId)) byParent.set(o.parentAccountId, []);
      byParent.get(o.parentAccountId).push(o);
    } else {
      tops.push(o);
    }
  });
  const ordered: any[] = [];
  const emitted = new Set<string>();
  tops.sort(sortAlphabetically).forEach(t => {
    ordered.push(t);
    emitted.add(t.id);
    (byParent.get(t.id) ?? []).sort(sortAlphabetically).forEach(c => {
      ordered.push(c);
      emitted.add(c.id);
    });
  });
  options.forEach(o => {
    if (!emitted.has(o.id)) ordered.push(o);
  });
  return ordered;
}

export const useUserStates = () => {
  const { data: userProfile = {} as UserRead } = userProfilesApi.endpoints.getCurrentUserProfile.useQueryState();

  const currentUserRolesSelector = res =>
    useMemo(() => {
      const accounts = res.data ? res.data.accounts?.filter(acc => acc.accountId === res.data.currentAccount) : [];
      const firstAccountInfo = accounts.length > 0 ? accounts[0] : {};
      return {
        currentUserRoles: firstAccountInfo.roles ? firstAccountInfo.roles : [],
      };
    }, [res.data]);

  const { currentUserRoles } = userProfilesApi.endpoints.getCurrentUserProfile.useQueryState(undefined, {
    selectFromResult: currentUserRolesSelector,
  });

  const accountsSelector = res =>
    useMemo(() => {
      return {
        accountOptions: res.data
          ? groupByParent(
              buildAccounts(userProfile.accounts, res.data).map(i => ({
                label: i.name || i.accountId.substring(0, 8),
                id: i.accountId,
                type: i.type ?? null,
                roles: i.roles ?? [],
                parentAccountId: i.parentAccountId ?? null,
                inherited: i.inherited ?? false,
              })),
            ).concat([
              { label: 'Create organization', id: '-1', type: null, roles: [], parentAccountId: null, inherited: false },
            ])
          : [],
        allUserAccounts: res.data ? buildAccounts(userProfile.accounts, res.data) : [],
      };
    }, [res.data]);

  const { accountOptions, allUserAccounts } = useGetCurrentUserAccountsQuery(userProfile.id ? undefined : skipToken, {
    selectFromResult: accountsSelector,
  });
  return {
    userProfile,
    currentUserRoles,
    accountOptions,
    allUserAccounts,
    selectedAccount: accountOptions.find(i => i.id === userProfile.currentAccount) || null,
  };
};
